/**
 * Operaciones — per-investor admin workspace (PR #7).
 *
 * Top-level orchestrator: pick an investor → pick a villa if they have more
 * than one → operate over Documentos / Pagos / Progreso / Reportes /
 * Novedades tabs. All tabs reuse existing backend endpoints; no new "god"
 * endpoint here.
 */
import React, { useEffect, useMemo, useState } from "react";
import AdminShell from "@/pages/admin/AdminShell";
import { api, formatError } from "@/lib/api";
import { Briefcase, FileText, Coin, Stack, ClipboardText, Pulse } from "@phosphor-icons/react";

import InvestorPicker from "@/components/admin/operaciones/InvestorPicker";
import DocumentosTab from "@/components/admin/operaciones/DocumentosTab";
import PagosTab from "@/components/admin/operaciones/PagosTab";
import ProgresoTab from "@/components/admin/operaciones/ProgresoTab";
import ReportesTab from "@/components/admin/operaciones/ReportesTab";
import NovedadesTab from "@/components/admin/operaciones/NovedadesTab";

const TABS = [
  { key: "documentos", label: "Documentos", icon: FileText },
  { key: "pagos",      label: "Pagos",      icon: Coin },
  { key: "progreso",   label: "Progreso",   icon: Stack },
  { key: "reportes",   label: "Reportes",   icon: ClipboardText },
  { key: "novedades",  label: "Novedades",  icon: Pulse },
];

export default function OperacionesPage() {
  const [users, setUsers] = useState([]);
  const [investorId, setInvestorId] = useState(null);
  const [villas, setVillas] = useState([]);
  const [villaId, setVillaId] = useState(null);
  const [loadingVillas, setLoadingVillas] = useState(false);
  const [tab, setTab] = useState("documentos");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        const investors = (data || []).filter((u) => u.role === "investor" && u.is_active !== false);
        setUsers(investors);
      } catch (e) {
        setError(formatError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!investorId) { setVillas([]); setVillaId(null); return; }
    setLoadingVillas(true);
    (async () => {
      try {
        const { data: all } = await api.get("/villas");
        const list = (all || []).filter(
          (v) => v.owner_user_id === investorId,
        );
        setVillas(list);
        if (list.length === 1) setVillaId(list[0].villa_id);
        else if (list.length === 0) setVillaId(null);
        else setVillaId((cur) => (list.find((v) => v.villa_id === cur) ? cur : null));
      } catch (e) {
        setError(formatError(e));
        setVillas([]);
        setVillaId(null);
      } finally {
        setLoadingVillas(false);
      }
    })();
  }, [investorId]);

  const investor = useMemo(
    () => users.find((u) => u.user_id === investorId) || null,
    [users, investorId],
  );
  const villa = useMemo(
    () => villas.find((v) => v.villa_id === villaId) || null,
    [villas, villaId],
  );

  return (
    <AdminShell>
      <div className="mb-5">
        <div className="flex items-center gap-2 text-stone-500 text-xs tracking-widest uppercase">
          <Briefcase size={14} /> Admin · Operaciones
        </div>
        <h2 className="font-serif-dv text-3xl leading-tight mt-1">Operaciones</h2>
        <p className="text-stone-500 mt-1 text-sm">
          Gestiona documentos, pagos, progreso y reportes para cada inversor.
        </p>
      </div>

      {error && (
        <div className="mb-4 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800" data-testid="operaciones-error">
          {error}
        </div>
      )}

      <div className="dv-card mb-5 grid sm:grid-cols-2 gap-3" data-testid="operaciones-pickers">
        <div>
          <div className="text-xs text-stone-500 mb-1">Inversor</div>
          <InvestorPicker
            users={users}
            value={investorId}
            onChange={(id) => { setInvestorId(id); setVillaId(null); }}
          />
        </div>
        <div>
          <div className="text-xs text-stone-500 mb-1">Villa</div>
          <select
            className="dv-input w-full"
            value={villaId || ""}
            onChange={(e) => setVillaId(e.target.value || null)}
            disabled={!investorId || loadingVillas || villas.length === 0}
            data-testid="operaciones-villa-select"
          >
            {!investorId && <option value="">Selecciona un inversor primero</option>}
            {investorId && loadingVillas && <option value="">Cargando villas…</option>}
            {investorId && !loadingVillas && villas.length === 0 && (
              <option value="">Sin villas asignadas</option>
            )}
            {investorId && !loadingVillas && villas.length > 0 && (
              <>
                <option value="">— Selecciona una villa —</option>
                {villas.map((v) => (
                  <option key={v.villa_id} value={v.villa_id}>
                    {v.name || v.villa_id} {v.model ? `· ${v.model}` : ""}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
      </div>

      {!investorId && (
        <div className="dv-card text-center py-10 text-stone-500 text-sm">
          Selecciona un inversor para empezar.
        </div>
      )}

      {investorId && (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="operaciones-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`operaciones-tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm transition-all ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "documentos" && (
            <DocumentosTab investor={investor} villa={villa} villas={villas} />
          )}
          {tab === "pagos" && (
            <PagosTab investor={investor} villa={villa} villas={villas} />
          )}
          {tab === "progreso" && (
            <ProgresoTab investor={investor} villa={villa} villas={villas} />
          )}
          {tab === "reportes" && (
            <ReportesTab investor={investor} villa={villa} villas={villas} />
          )}
          {tab === "novedades" && (
            <NovedadesTab investor={investor} villas={villas} />
          )}
        </>
      )}
    </AdminShell>
  );
}
